/* ═══════════════════════════════════════════════════════════════
   REVIEW — the Publish self-review walk, shared (SYNC D3 / D14).

   One implementation for the spec Studio and the module builder: compare
   what is on the canvas (OURS) against the published blocks (THEIRS), paint
   the result inverted with the Review plugin, and hand Previous / Next /
   Continue to a ReviewBar in 'walk' mode. Continue runs the caller's publish
   step AFTER the walk is closed.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import type { Change } from '../../shared/sync';
import type { ContentBlock } from '../../shared/types';
import { docToBlocks } from '../designer/schema';
import { walkChanges } from './walk';
import { useChangeWalker } from './useReview';
import { useReviewDecorations } from './useReviewDecorations';
import type { ReviewPluginInput } from './reviewPlugin';

/** Stable empty list for the walker. */
const NO_CHANGES: Change[] = [];

/** Changes between the published blocks and what the canvas shows. The
 *  canvas wins as OURS (so offsets index the document on screen); the
 *  fallback is used only when no live editor exists. */
export function publishWalkChanges(
  editor: Editor | null,
  fallbackOnScreen: ContentBlock[],
  published: ContentBlock[]
): Change[] {
  const onScreen = editor && !editor.isDestroyed ? docToBlocks(editor.getJSON()) : fallbackOnScreen;
  return walkChanges(onScreen, published);
}

export function usePublishWalk(editor: Editor | null) {
  const [walk, setWalk] = React.useState<Change[] | null>(null);
  const walker = useChangeWalker(walk ?? NO_CHANGES);
  const current = walker.current;
  const input = React.useMemo<ReviewPluginInput | null>(
    () =>
      walk ? { changes: walk, decisions: {}, currentId: current?.id ?? null, invert: true } : null,
    [walk, current]
  );
  useReviewDecorations(editor, input);

  const editorRef = React.useRef(editor);
  editorRef.current = editor;

  /** Walk when anything differs; otherwise call `onEmpty` (straight to the
   *  confirm). `onWalk` runs just before the walk opens. */
  const start = React.useCallback(
    (
      published: ContentBlock[],
      fallbackOnScreen: ContentBlock[],
      onEmpty: () => void,
      onWalk?: () => void
    ) => {
      const changes = publishWalkChanges(editorRef.current, fallbackOnScreen, published);
      if (!changes.length) {
        onEmpty();
        return;
      }
      onWalk?.();
      setWalk(changes);
    },
    []
  );
  /** Close the walk, then run the publish step. */
  const finish = React.useCallback((onContinue: () => void) => {
    setWalk(null);
    onContinue();
  }, []);
  const leave = React.useCallback(() => setWalk(null), []);
  return { walk, setWalk, walker, start, finish, leave };
}
